import { useEffect, useState } from "react";
import type { ThemeDocument } from "@/api/queries";
import { Chip } from "@/components/ui/Chip";
import { Field } from "@/components/ui/Field";
import { Select } from "@/components/ui/Select";
import { FONT_PRESETS, FONT_ROLES, type FontRole, type FontSpec } from "@/theme/fontPresets";

/**
 * The three typefaces (`brand.font.*`): headings, text and code.
 *
 * The backend always accepted these; the page never offered them, so a
 * rebrand kept the platform's Garamond headings. A preset is one click;
 * "Custom" takes any family, fallback stack and https webfont URL. The type
 * *scale* is platform-level and is not here: a new face keeps the rhythm.
 */

export type FontForm = Record<FontRole, FontSpec | null>;

const CUSTOM = "__custom__";
const SAMPLE_TYPE: Record<FontRole, string> = {
  display: "type-h2",
  body: "type-body",
  mono: "type-code",
};
const FILE = "";

/**
 * Load a preset's stylesheet so its sample renders before anything is saved.
 * Google Fonts only — the page's CSP already admits it, because the shipped
 * theme loads from there.
 */
function usePreviewFont(source: string | null | undefined) {
  useEffect(() => {
    if (!source || !source.startsWith("https://fonts.googleapis.com/")) return;
    if (document.querySelector(`link[data-font-preview="${CSS.escape(source)}"]`)) return;
    const link = document.createElement("link");
    link.rel = "stylesheet";
    link.href = source;
    link.dataset.fontPreview = source;
    document.head.append(link);
  }, [source]);
}

function RoleEditor({
  role,
  label,
  hint,
  sample,
  chosen,
  effective,
  disabled,
  onChange,
}: {
  role: FontRole;
  label: string;
  hint: string;
  sample: string;
  chosen: FontSpec | null;
  effective: FontSpec | undefined;
  disabled?: boolean;
  onChange: (next: FontSpec | null) => void;
}) {
  const shown = chosen ?? effective;
  usePreviewFont(shown?.source);
  const preset = chosen ? FONT_PRESETS.find((item) => item.family === chosen.family) : undefined;
  // Chosen explicitly, not inferred: the shipped faces *are* presets, so
  // "Custom…" seeded from one would otherwise snap straight back to its name
  // and never show the fields it was chosen for.
  const [custom, setCustom] = useState(
    () => Boolean(chosen) && !(preset && preset.stack === chosen?.stack),
  );
  const selected = !chosen ? FILE : custom ? CUSTOM : (preset?.family ?? CUSTOM);

  return (
    <div className="border-border rounded-element flex flex-col gap-3 border p-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="type-label text-ink">{label}</span>
        {chosen ? <Chip tone="accent">set here</Chip> : <Chip>from tokens.json</Chip>}
        <span className="type-support text-ink-secondary basis-full">{hint}</span>
      </div>
      <Select
        label={`${label} typeface`}
        value={selected}
        disabled={disabled}
        onChange={(event) => {
          const value = event.target.value;
          setCustom(value === CUSTOM);
          if (value === FILE) onChange(null);
          else if (value === CUSTOM)
            onChange(shown ? { ...shown, source: shown.source ?? null } : { family: "", stack: "", source: null });
          else {
            const next = FONT_PRESETS.find((item) => item.family === value);
            if (next) onChange({ family: next.family, stack: next.stack, source: next.source });
          }
        }}
      >
        <option value={FILE}>
          The file's{effective?.family ? ` — ${effective.family}` : ""}
        </option>
        {(["serif", "sans", "mono"] as const).map((kind) => (
          <optgroup
            key={kind}
            label={kind === "serif" ? "Serif" : kind === "sans" ? "Sans-serif" : "Monospace"}
          >
            {FONT_PRESETS.filter((item) => item.kind === kind).map((item) => (
              <option key={item.family} value={item.family}>
                {item.family}
              </option>
            ))}
          </optgroup>
        ))}
        <option value={CUSTOM}>Custom…</option>
      </Select>
      {selected === CUSTOM && chosen ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label="Family"
            value={chosen.family}
            disabled={disabled}
            onChange={(event) => onChange({ ...chosen, family: event.target.value })}
          />
          <Field
            label="Fallback stack"
            hint="Used while the webfont loads, or if it cannot."
            value={chosen.stack}
            disabled={disabled}
            onChange={(event) => onChange({ ...chosen, stack: event.target.value })}
          />
          <div className="sm:col-span-2">
            <Field
              label="Webfont stylesheet URL"
              hint="An https:// CSS URL, e.g. from Google Fonts. Leave empty for a font installed on every reader's machine. A font from a new host appears after the page next loads."
              value={chosen.source ?? ""}
              disabled={disabled}
              onChange={(event) => onChange({ ...chosen, source: event.target.value || null })}
            />
          </div>
        </div>
      ) : null}
      {/* The role's own type style, with only the family swapped in — the
          scale is the platform's, so the sample is exactly what will ship. */}
      <p
        className={`text-ink bg-surface rounded-inner px-3 py-2 ${SAMPLE_TYPE[role]}`}
        style={{ fontFamily: shown?.stack || undefined }}
      >
        {sample}
      </p>
    </div>
  );
}

export function FontEditor({
  form,
  document,
  disabled,
  onChange,
}: {
  form: FontForm;
  document: ThemeDocument;
  disabled?: boolean;
  onChange: (next: FontForm) => void;
}) {
  const fonts = document.brand.font as unknown as Record<FontRole, FontSpec | undefined>;
  return (
    <div className="flex flex-col gap-3">
      {FONT_ROLES.map((item) => (
        <RoleEditor
          key={item.role}
          {...item}
          chosen={form[item.role]}
          effective={fonts[item.role]}
          disabled={disabled}
          onChange={(next) => onChange({ ...form, [item.role]: next })}
        />
      ))}
    </div>
  );
}
